/**
 * TKS Claim Vault ABI and Configuration
 */

export const TKS_CLAIM_VAULT_ABI = [
    // View Functions
    {
        inputs: [{ name: "user", type: "address" }],
        name: "getAvailableToClaim",
        outputs: [{ name: "", type: "uint256" }],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [{ name: "user", type: "address" }],
        name: "getUserInfo",
        outputs: [
            { name: "totalClaimableAmount", type: "uint256" },
            { name: "alreadyClaimed", type: "uint256" },
            { name: "availableToClaim", type: "uint256" },
            { name: "nonce", type: "uint256" },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [],
        name: "getVaultStats",
        outputs: [
            { name: "tksBalance", type: "uint256" },
            { name: "totalClaimableAmount", type: "uint256" },
            { name: "totalClaimedAmount", type: "uint256" },
            { name: "claimerCount", type: "uint256" },
            { name: "isClaimingEnabled", type: "bool" },
        ],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [{ name: "user", type: "address" }],
        name: "claimable",
        outputs: [{ name: "", type: "uint256" }],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [{ name: "user", type: "address" }],
        name: "claimed",
        outputs: [{ name: "", type: "uint256" }],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [],
        name: "claimingEnabled",
        outputs: [{ name: "", type: "bool" }],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [],
        name: "owner",
        outputs: [{ name: "", type: "address" }],
        stateMutability: "view",
        type: "function",
    },
    {
        inputs: [{ name: "provider", type: "address" }],
        name: "isAuthorizedProvider",
        outputs: [{ name: "", type: "bool" }],
        stateMutability: "view",
        type: "function",
    },
    // Write Functions
    {
        inputs: [],
        name: "claim",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [{ name: "amount", type: "uint256" }, { name: "nonce", type: "uint256" }, { name: "signature", type: "bytes" }],
        name: "claim",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [{ name: "amount", type: "uint256" }],
        name: "claimAmount",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [{ name: "payout", type: "uint256" }, { name: "nonce", type: "uint256" }, { name: "signature", type: "bytes" }],
        name: "claimExact",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    // Events
    {
        anonymous: false,
        inputs: [
            { indexed: true, name: "user", type: "address" },
            { indexed: false, name: "amount", type: "uint256" },
        ],
        name: "Claimed",
        type: "event",
    },
    {
        anonymous: false,
        inputs: [
            { indexed: true, name: "user", type: "address" },
            { indexed: true, name: "recipient", type: "address" },
            { indexed: false, name: "amount", type: "uint256" },
        ],
        name: "ClaimedTo",
        type: "event",
    },
    // Owner function for non-EVM claims
    {
        inputs: [
            { name: "user", type: "address" },
            { name: "amount", type: "uint256" },
        ],
        name: "setClaimable",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
    {
        inputs: [
            { name: "user", type: "address" },
            { name: "recipient", type: "address" },
            { name: "amount", type: "uint256" },
        ],
        name: "claimTo",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
    },
] as const

// Claim Vault addresses (deployed) — configurable via env vars
export const TKS_CLAIM_VAULT_ADDRESSES: Record<number, `0x${string}` | null> = {
    // Mainnet
    56: (process.env.NEXT_PUBLIC_TKS_VAULT_BSC || null) as `0x${string}` | null,          // BSC Mainnet
    1: (process.env.NEXT_PUBLIC_TKS_VAULT_ETH || null) as `0x${string}` | null,           // Ethereum Mainnet
    // Testnet
    97: (process.env.NEXT_PUBLIC_TKS_VAULT_BSC_TESTNET || "0xacCB3Fac4724Ed22181Ba484E6bd0F0965924103") as `0x${string}`,       // BSC Testnet
    11155111: (process.env.NEXT_PUBLIC_TKS_VAULT_SEPOLIA || "0xacCB3Fac4724Ed22181Ba484E6bd0F0965924103") as `0x${string}`, // Sepolia
    // Not supported for claiming
    137: null,     // Polygon Mainnet
    8453: null,    // Base Mainnet
    80002: null,   // Polygon Amoy
    84532: null,   // Base Sepolia
}

// Networks where claiming is enabled
export const CLAIM_NETWORKS = [56, 1, 97, 11155111] // BSC, ETH, BSC Testnet, Sepolia

// TKS Token address — configurable via env var
export const TKS_TOKEN_ADDRESS = process.env.NEXT_PUBLIC_TKS_TOKEN_ADDRESS || "0x8664E4345E2181032F1dDfbCCE210456C0eD8401"

